import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const NotificationPanel = ({ userId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [unreadCounts, setUnreadCounts] = useState({});
  const [loading, setLoading] = useState(false);

  const categories = [
    { id: 'all', label: 'All', icon: 'Bell' },
    { id: 'session', label: 'Sessions', icon: 'Video' },
    { id: 'chat', label: 'Messages', icon: 'MessageSquare' },
    { id: 'event', label: 'Events', icon: 'Calendar' },
    { id: 'institute_response', label: 'Responses', icon: 'Reply' },
    { id: 'system', label: 'System', icon: 'Settings' }
  ];

  // Load notifications
  const loadNotifications = async () => {
    if (!userId) return;
    
    setLoading(true);
    try {
      const { notificationService } = await import('../../services/notificationService');
      const options = activeCategory !== 'all' ? { category: activeCategory } : {};
      const data = await notificationService?.getNotifications(userId, options);
      setNotifications(data || []);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load unread counts for all categories
  const loadUnreadCounts = async (signal) => {
    if (!userId) return;
    
    try {
      const { notificationService } = await import('../../services/notificationService');
      const counts = {};
      
      // Get total unread count
      counts.all = await notificationService?.getUnreadCount(userId, null, signal);
      
      // Get unread count per category
      for (const cat of categories) {
        if (cat?.id !== 'all') {
          // Check if signal is aborted before making next request
          if (signal?.aborted) break;
          counts[cat?.id] = await notificationService?.getUnreadCount(userId, cat?.id, signal);
        }
      }
      
      // Only update state if not aborted
      if (!signal?.aborted) {
        setUnreadCounts(counts);
      }
    } catch (error) {
      // Ignore abort errors
      if (error?.name !== 'AbortError') {
        console.error('Error loading unread counts:', error);
      }
    }
  };

  // Initial load
  useEffect(() => {
    if (userId) {
      const abortController = new AbortController();
      loadNotifications();
      loadUnreadCounts(abortController?.signal);
      
      return () => {
        abortController?.abort();
      };
    }
  }, [userId, activeCategory]);

  // Refresh every 30 seconds when panel is open
  useEffect(() => {
    if (!isOpen || !userId) return;
    
    const abortController = new AbortController();
    const interval = setInterval(() => {
      loadNotifications();
      loadUnreadCounts(abortController?.signal);
    }, 30000);
    
    return () => {
      clearInterval(interval);
      abortController?.abort();
    };
  }, [isOpen, userId, activeCategory]);

  const handleMarkAsRead = async (notificationId) => {
    try {
      const { notificationService } = await import('../../services/notificationService');
      await notificationService?.markAsRead(notificationId);
      
      // Update local state
      setNotifications(prev => 
        prev?.map(n => n?.id === notificationId ? { ...n, isRead: true, readAt: new Date()?.toISOString() } : n)
      );
      
      // Reload unread counts
      loadUnreadCounts();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const { notificationService } = await import('../../services/notificationService');
      const category = activeCategory !== 'all' ? activeCategory : null;
      await notificationService?.markAllAsRead(userId, category);
      
      // Reload notifications and counts
      loadNotifications();
      loadUnreadCounts();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    // Mark as read
    if (!notification?.isRead) {
      handleMarkAsRead(notification?.id);
    }
    
    // Navigate to action URL if exists
    if (notification?.actionUrl) {
      window.location.href = notification?.actionUrl;
    }
  };

  const getNotificationIcon = (category) => {
    const cat = categories?.find(c => c?.id === category);
    return cat?.icon || 'Bell';
  };

  const getNotificationColor = (category, priority) => {
    if (priority === 'urgent') return 'text-red-500';
    if (priority === 'high') return 'text-orange-500';
    
    switch (category) {
      case 'session':
        return 'text-blue-500';
      case 'chat':
        return 'text-green-500';
      case 'event':
        return 'text-purple-500';
      case 'institute_response':
        return 'text-indigo-500';
      case 'system':
        return 'text-gray-500';
      default:
        return 'text-muted-foreground';
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    // Supabase returns UTC timestamps without 'Z' suffix — normalize so JS parses as UTC
    const ts = typeof timestamp === 'string' && !timestamp.endsWith('Z') && !timestamp.includes('+')
      ? timestamp + 'Z'
      : timestamp;
    const notificationTime = new Date(ts);
    const diffInMinutes = Math.floor((now - notificationTime) / 60000);

    if (diffInMinutes < 0) return 'Just now';
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const totalUnread = unreadCounts?.all || 0;
  const filteredNotifications = notifications || [];
  const hasUnread = filteredNotifications?.some(n => !n?.isRead);

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="icon"
        className="relative"
        onClick={() => setIsOpen(!isOpen)}
      >
        <Icon name="Bell" size={20} />
        {totalUnread > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-semibold rounded-full flex items-center justify-center">
            {totalUnread > 99 ? '99+' : totalUnread}
          </span>
        )}
      </Button>
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-1010" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 top-full mt-2 w-96 max-h-[600px] bg-popover border border-border rounded-lg shadow-xl z-1010 overflow-hidden flex flex-col">
            {/* Header */}
            <div className="sticky top-0 bg-popover border-b border-border px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="Bell" size={20} color="var(--color-primary)" />
                <h3 className="font-semibold text-foreground">Notifications</h3>
                {totalUnread > 0 && (
                  <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-semibold rounded-full">
                    {totalUnread}
                  </span>
                )}
              </div>
              {hasUnread && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-sm text-primary hover:text-primary/80 transition-smooth font-medium"
                >
                  Mark all read
                </button>
              )}
            </div>

            {/* Category Tabs */}
            <div className="sticky top-[57px] bg-popover border-b border-border px-2 py-2 flex gap-1 overflow-x-auto">
              {categories?.map((cat) => {
                const count = unreadCounts?.[cat?.id] || 0;
                const isActive = activeCategory === cat?.id;
                
                return (
                  <button
                    key={cat?.id}
                    onClick={() => setActiveCategory(cat?.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-smooth ${
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    <Icon name={cat?.icon} size={14} />
                    <span>{cat?.label}</span>
                    {count > 0 && (
                      <span className={`px-1.5 py-0.5 text-xs font-semibold rounded-full ${
                        isActive ? 'bg-primary-foreground text-primary' : 'bg-red-500 text-white'
                      }`}>
                        {count > 99 ? '99+' : count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Notifications List */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-12 px-4">
                  <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                  <p className="mt-3 text-sm text-muted-foreground">Loading notifications...</p>
                </div>
              ) : filteredNotifications?.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-4">
                  <Icon name="BellOff" size={48} color="var(--color-muted-foreground)" />
                  <p className="mt-3 text-sm text-muted-foreground text-center">
                    No {activeCategory !== 'all' ? activeCategory : ''} notifications yet
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {filteredNotifications?.map((notification) => (
                    <div
                      key={notification?.id}
                      className={`px-4 py-3 hover:bg-muted transition-smooth cursor-pointer ${
                        !notification?.isRead ? 'bg-primary/5' : ''
                      }`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="flex gap-3">
                        <div className={`flex-shrink-0 ${getNotificationColor(notification?.category, notification?.priority)}`}>
                          <Icon name={getNotificationIcon(notification?.category)} size={20} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm font-medium text-foreground line-clamp-2">
                              {notification?.title}
                            </p>
                            {!notification?.isRead && (
                              <div className="flex-shrink-0 w-2 h-2 bg-red-500 rounded-full mt-1" />
                            )}
                          </div>
                          <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
                            {notification?.message}
                          </p>
                          <div className="mt-2 flex items-center gap-2">
                            <p className="text-xs text-muted-foreground">
                              {formatTime(notification?.createdAt)}
                            </p>
                            {notification?.priority === 'urgent' && (
                              <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-semibold rounded">
                                URGENT
                              </span>
                            )}
                            {notification?.priority === 'high' && (
                              <span className="px-2 py-0.5 bg-orange-500 text-white text-xs font-semibold rounded">
                                HIGH
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {filteredNotifications?.length > 0 && (
              <div className="sticky bottom-0 bg-popover border-t border-border px-4 py-3">
                <Button
                  variant="outline"
                  fullWidth
                  size="sm"
                  onClick={() => {
                    setIsOpen(false);
                    // Could navigate to a full notifications page
                  }}
                >
                  View All Notifications
                </Button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationPanel;